import React, { useState } from 'react';
import { Modal } from 'antd';
import { submitSupportRequest } from '../../../api';
import messageQueue from '../../../utils/messageQueue';
import MessageConstant from '../../../utils/Constant/MessageConstant';
import styles from './BugReportModal.module.scss';

const BugReportModal = ({ visible, onClose }) => {
    const [bugType, setBugType] = useState('');
    const [severity, setSeverity] = useState('');
    const [description, setDescription] = useState('');
    const [steps, setSteps] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const bugTypes = [
        'UI/Display Issue',
        'Functionality Error',
        'Performance Issue',
        'Data Loss',
        'Other'
    ];

    const severityLevels = [
        { value: 'low', label: 'Low - Minor inconvenience', color: '#16a34a' },
        { value: 'medium', label: 'Medium - Affects functionality', color: '#f59e0b' },
        { value: 'high', label: 'High - Major issue', color: '#dc464f' },
        { value: 'critical', label: 'Critical - Blocks usage', color: '#991b1b' }
    ];

    const handleCancel = () => {
        setBugType('');
        setSeverity('');
        setDescription('');
        setSteps('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!bugType || !severity || !description) {
            messageQueue.warning('VALIDATION_REQUIRED_FIELDS', MessageConstant.VALIDATION_REQUIRED_FIELDS);
            return;
        }

        setIsSubmitting(true);
        try {
            await submitSupportRequest('bug', {
                bugType,
                severity,
                description,
                steps,
                currentPage: window.location.pathname,
                currentUrl: window.location.href,
                userAgent: navigator.userAgent,
            });
            messageQueue.success('BUG_REPORT_SUCCESS', MessageConstant.BUG_REPORT_SUCCESS);
            handleCancel();
        } catch (error) {
            messageQueue.error('BUG_REPORT_ERROR', error.response?.data?.error || 'Failed to submit bug report. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            centered
            width={700}
            className={styles.bugReportModal}
        >
            <div className={styles.modalContent}>
                {/* Header */}
                <div className={styles.header}>
                    <svg className={styles.bugIcon} width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="20" fill="#FEF3F2" />
                        <path d="M24 14V24M24 30H24.02" stroke="#dc464f" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M24 38C31.732 38 38 31.732 38 24C38 16.268 31.732 10 24 10C16.268 10 10 16.268 10 24C10 31.732 16.268 38 24 38Z" stroke="#dc464f" strokeWidth="2" />
                    </svg>
                    <h2 className={styles.title}>Report a Bug</h2>
                    <p className={styles.subtitle}>Help us improve Prepal by reporting bugs you encounter</p>
                </div>

                {/* Bug Type */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Bug Type <span className={styles.required}>*</span></label>
                    <div className={styles.optionsList}>
                        {bugTypes.map((type) => (
                            <div
                                key={type}
                                className={`${styles.optionItem} ${bugType === type ? styles.selected : ''}`}
                                onClick={() => setBugType(type)}
                            >
                                <div className={styles.radioButton}>
                                    {bugType === type && <div className={styles.radioInner} />}
                                </div>
                                <span className={styles.optionText}>{type}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Severity */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Severity Level <span className={styles.required}>*</span></label>
                    <div className={styles.severityList}>
                        {severityLevels.map((level) => (
                            <div
                                key={level.value}
                                className={`${styles.severityItem} ${severity === level.value ? styles.selected : ''}`}
                                onClick={() => setSeverity(level.value)}
                                style={{ '--severity-color': level.color }}
                            >
                                <div className={styles.severityIndicator} />
                                <span className={styles.severityText}>{level.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Description */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Bug Description <span className={styles.required}>*</span></label>
                    <textarea
                        className={styles.textArea}
                        placeholder="Describe what happened and what you expected to happen..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                    />
                </div>

                {/* Steps to Reproduce */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Steps to Reproduce (Optional)</label>
                    <textarea
                        className={styles.textArea}
                        placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                        value={steps}
                        onChange={(e) => setSteps(e.target.value)}
                        rows={4}
                    />
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    <button className={styles.cancelButton} onClick={handleCancel}>
                        Cancel
                    </button>
                    <button
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={!bugType || !severity || !description || isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default BugReportModal;
